/**
 * DrugRegistryManager — качване на официалните лекарствени регистри (.xlsx).
 * Backend: POST /api/medications/registry/import-xlsx (само администратор)
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Database, Upload, Loader2, Search } from "lucide-react";

export default function DrugRegistryManager({ API, isDark }) {
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [q, setQ] = useState("");
  const [hits, setHits] = useState([]);
  const fileRef = useRef(null);

  const probe = useCallback(async (term) => {
    if (term.trim().length < 2) { setHits([]); return; }
    try {
      const { data } = await axios.get(`${API}/medications/registry/search`,
        { params: { q: term.trim() } });
      setHits(data?.items || []);
    } catch { setHits([]); }
  }, [API]);

  useEffect(() => {
    const t = setTimeout(() => probe(q), 400);
    return () => clearTimeout(t);
  }, [q, probe]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/medications/registry/import-xlsx`, fd,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 300000 });
      setResult(data);
      toast.success(`Внесени ${data.unique_rows} лекарства · общо ${data.registry_total}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Вносът не успя");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const cardCls = isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200";
  const inputCls = `px-3 py-2 rounded-lg text-[13px] border w-full ${
    isDark ? "bg-slate-800 border-slate-700" : "bg-white border-slate-300"}`;

  return (
    <div className={`p-5 rounded-2xl border ${cardCls}`} data-testid="admin-drug-registry">
      <h2 className="text-base font-bold flex items-center gap-2">
        <Database className="w-4 h-4 text-violet-500" />
        Лекарствен указател (ИАЛ и централно разрешени)
      </h2>
      <p className="text-[12px] opacity-65 mt-1">
        Качете таблицата от Изпълнителната агенция по лекарствата или списъка на
        централно разрешените продукти. Разпознават се колоните за търговско име,
        активно вещество, доза, форма и код по анатомо-терапевтичната класификация.
      </p>

      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <input ref={fileRef} type="file" accept=".xlsx"
          onChange={(e) => upload(e.target.files?.[0])}
          className="hidden" data-testid="admin-registry-file" />
        <button onClick={() => fileRef.current?.click()} disabled={uploading}
          className="text-[12px] font-bold px-4 py-2 rounded-xl bg-violet-500 text-white
                     hover:bg-violet-600 disabled:opacity-60 inline-flex items-center gap-1.5"
          data-testid="admin-registry-upload-btn">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {uploading ? "Внасям…" : "Качи таблица (.xlsx)"}
        </button>
        {result && (
          <span className="text-[12px] opacity-80" data-testid="admin-registry-result">
            {result.filename}: {result.unique_rows} лекарства, пропуснати {result.skipped},
            общо в указателя {result.registry_total}
          </span>
        )}
      </div>

      <div className="mt-4">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-2.5 opacity-40" />
          <input className={`${inputCls} pl-9`} value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Проверка: напишете име (например Енап, Аторва, Metformin)"
            data-testid="admin-registry-search" />
        </div>
        {!!hits.length && (
          <div className="mt-2 space-y-1" data-testid="admin-registry-hits">
            {hits.map((h) => (
              <div key={`${h.name}-${h.strength}`}
                className={`text-[12px] px-3 py-2 rounded-lg ${isDark ? "bg-white/5" : "bg-slate-50"}`}>
                <span className="font-semibold">{h.name}</span>
                <span className="opacity-60"> · {h.ingredient} · {h.strength || "без доза"} · {h.form || "—"} · {h.atc || "—"}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
